import { useEffect, useMemo, useRef, useState } from "react";
import { Camera, ExternalLink, FilePlus2, FileText, Image, Link2, Pencil, RefreshCw, Search, ShieldCheck, Trash2, Upload } from "lucide-react";
import {
  currentDocumentRole, deletePrivateDocument, loadDocuments, loadDocumentTargets,
  openDocument, registerExternalDocument, updateDocumentMetadata, uploadPrivateDocument,
} from "./documentService";
import type { DocumentRow, DocumentTarget } from "./documentService";
import "./documentsWorkspace.css";

const TYPES = [
  ["nameplate_photo", "Nameplate Photo"],
  ["equipment_photo", "Equipment Photo"],
  ["part_photo", "Part Photo"],
  ["oem_manual", "OEM Manual"],
  ["datasheet", "Datasheet"],
  ["mechanical_drawing", "Mechanical Drawing"],
  ["electrical_drawing", "Electrical Drawing"],
  ["quote", "Quote"],
  ["repair_report", "Repair Report"],
  ["inspection_report", "Inspection Report"],
  ["cross_reference", "Cross Reference"],
  ["cad_3d", "CAD / 3D Reference"],
  ["other", "Other"],
] as const;

const DELETE_ROLES = new Set(["maintenance_manager", "engineering", "administrator"]);

function typeLabel(value: string) {
  return TYPES.find(([key]) => key === value)?.[1] ?? value.replaceAll("_", " ");
}

export default function DocumentsWorkspace() {
  const [targets, setTargets] = useState<DocumentTarget[]>([]);
  const [documents, setDocuments] = useState<DocumentRow[]>([]);
  const [role, setRole] = useState("viewer");
  const [targetKind, setTargetKind] = useState<"all" | "asset" | "part">("all");
  const [targetId, setTargetId] = useState("");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const photoRef = useRef<HTMLInputElement>(null);

  async function reload() {
    setBusy(true);
    setMessage("");
    try {
      const [nextTargets, nextDocuments, nextRole] = await Promise.all([
        loadDocumentTargets(), loadDocuments(), currentDocumentRole(),
      ]);
      setTargets(nextTargets);
      setDocuments(nextDocuments);
      setRole(nextRole);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to load document library.");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { void reload(); }, []);

  const targetMap = useMemo(() => new Map(targets.map((target) => [target.id, target])), [targets]);
  const filteredTargets = useMemo(() => targetKind === "all" ? targets : targets.filter((target) => target.kind === targetKind), [targets, targetKind]);
  const filteredDocuments = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return documents.filter((document) => {
      const docTargetId = document.asset_id ?? document.part_id ?? "";
      if (targetId && docTargetId !== targetId) return false;
      if (targetKind === "asset" && !document.asset_id) return false;
      if (targetKind === "part" && !document.part_id) return false;
      if (!needle) return true;
      const target = targetMap.get(docTargetId);
      return [document.title, document.document_type, document.file_name, document.revision, document.notes, target?.label]
        .some((value) => value?.toLowerCase().includes(needle));
    });
  }, [documents, query, targetId, targetKind, targetMap]);

  function pickTarget(): DocumentTarget | null {
    const available = filteredTargets.length ? filteredTargets : targets;
    if (!available.length) {
      window.alert("No active assets or parts exist yet. Create the equipment or part record first.");
      return null;
    }
    const defaultLabel = targetId ? targetMap.get(targetId)?.label : available[0]?.label;
    const requested = window.prompt("Attach to which asset or part? Enter the exact label shown in the library:", defaultLabel ?? "");
    if (!requested) return null;
    const exact = targets.find((target) => target.label.toLowerCase() === requested.trim().toLowerCase());
    if (exact) return exact;
    const partialMatches = targets.filter((target) => target.label.toLowerCase().includes(requested.trim().toLowerCase()));
    if (partialMatches.length === 1) return partialMatches[0];
    window.alert(partialMatches.length > 1 ? "That matches more than one record. Enter a more specific asset tag or part number." : "No matching asset or part was found.");
    return null;
  }

  async function uploadFile(file: File) {
    const target = pickTarget();
    if (!target) return;
    const defaultType = file.type.startsWith("image/") ? (target.kind === "asset" ? "equipment_photo" : "part_photo") : "oem_manual";
    const documentType = window.prompt(`Document type (${TYPES.map(([, label]) => label).join(", ")}):`, typeLabel(defaultType));
    if (!documentType) return;
    const normalizedType = TYPES.find(([, label]) => label.toLowerCase() === documentType.trim().toLowerCase())?.[0] ?? documentType.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_");
    const title = window.prompt("Document title:", file.name.replace(/\.[^.]+$/, ""));
    if (!title) return;
    const revision = window.prompt("Revision (optional):", "") ?? "";
    const notes = window.prompt("Notes (optional):", "") ?? "";

    setBusy(true); setMessage("");
    try {
      await uploadPrivateDocument({ target, documentType: normalizedType, title, revision, notes, file });
      await reload();
      setMessage(`Uploaded ${file.name} securely.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Upload failed.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
      if (photoRef.current) photoRef.current.value = "";
    }
  }

  async function addExternalLink() {
    const target = pickTarget();
    if (!target) return;
    const url = window.prompt("External document URL:", "https://");
    if (!url || url === "https://") return;
    const documentType = window.prompt("Document type:", "OEM Manual"); if (!documentType) return;
    const normalizedType = TYPES.find(([, label]) => label.toLowerCase() === documentType.trim().toLowerCase())?.[0] ?? documentType.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_");
    const title = window.prompt("Document title:", "OEM reference"); if (!title) return;
    const revision = window.prompt("Revision (optional):", "") ?? "";
    const notes = window.prompt("Notes (optional):", "") ?? "";
    setBusy(true); setMessage("");
    try {
      await registerExternalDocument({ target, documentType: normalizedType, title, url, revision, notes });
      await reload(); setMessage("External document link registered.");
    } catch (error) { setMessage(error instanceof Error ? error.message : "Unable to save document link."); }
    finally { setBusy(false); }
  }

  async function editMetadata(document: DocumentRow) {
    const type = window.prompt("Document type:", typeLabel(document.document_type)); if (!type) return;
    const normalizedType = TYPES.find(([, label]) => label.toLowerCase() === type.trim().toLowerCase())?.[0] ?? type.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_");
    const title = window.prompt("Document title:", document.title); if (!title) return;
    const revision = window.prompt("Revision:", document.revision ?? "") ?? "";
    const notes = window.prompt("Notes:", document.notes ?? "") ?? "";
    setBusy(true); setMessage("");
    try { await updateDocumentMetadata(document.id, { documentType: normalizedType, title, revision, notes }); await reload(); setMessage("Document metadata updated."); }
    catch (error) { setMessage(error instanceof Error ? error.message : "Unable to update document."); }
    finally { setBusy(false); }
  }

  async function viewDocument(document: DocumentRow) {
    setMessage("");
    try { await openDocument(document); }
    catch (error) { setMessage(error instanceof Error ? error.message : "Unable to open document."); }
  }

  async function removeDocument(document: DocumentRow) {
    if (!window.confirm(`Delete “${document.title}”? This removes the private stored file and its library record.`)) return;
    const reason = window.prompt("Reason for deletion:", "Superseded or incorrect document") ?? "Technical document removed";
    setBusy(true); setMessage("");
    try { await deletePrivateDocument(document, reason); await reload(); setMessage("Document removed."); }
    catch (error) { setMessage(error instanceof Error ? error.message : "Unable to delete document."); }
    finally { setBusy(false); }
  }

  return <div className="documents-workspace">
    <header className="dw-header">
      <div><div className="dw-eyebrow">PLANT EQUIPMENT INTELLIGENCE</div><h1>Documents & Photos</h1><p>Private technical files attached to the exact equipment or replacement part they describe.</p></div>
      <button className="dw-secondary" disabled={busy} onClick={() => void reload()}><RefreshCw size={16}/> Refresh</button>
    </header>

    <div className="dw-security"><ShieldCheck size={17}/><div><strong>Private technical storage</strong><span>Authenticated access only · 50 MB per file · signed viewing links expire after 5 minutes</span></div></div>

    <div className="dw-toolbar">
      <div className="dw-search"><Search size={17}/><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search title, type, file name, tag, part number..."/></div>
      <select value={targetKind} onChange={(event) => { setTargetKind(event.target.value as typeof targetKind); setTargetId(""); }}><option value="all">All records</option><option value="asset">Assets</option><option value="part">Parts</option></select>
      <select value={targetId} onChange={(event) => setTargetId(event.target.value)}><option value="">All targets</option>{filteredTargets.map((target) => <option key={`${target.kind}-${target.id}`} value={target.id}>{target.label}</option>)}</select>
      <button className="dw-primary" disabled={busy} onClick={() => fileRef.current?.click()}><Upload size={16}/> Upload File</button>
      <button className="dw-primary" disabled={busy} onClick={() => photoRef.current?.click()}><Camera size={16}/> Take Photo</button>
      <button className="dw-secondary" disabled={busy} onClick={() => void addExternalLink()}><Link2 size={16}/> Add Link</button>
      <input ref={fileRef} className="dw-hidden" type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.dwg,.dxf" onChange={(event) => { const file = event.target.files?.[0]; if (file) void uploadFile(file); }}/>
      <input ref={photoRef} className="dw-hidden" type="file" accept="image/*" capture="environment" onChange={(event) => { const file = event.target.files?.[0]; if (file) void uploadFile(file); }}/>
    </div>

    {message && <div className="dw-message">{message}</div>}

    <div className="dw-summary"><strong>{filteredDocuments.length}</strong><span>matching documents</span><strong>{targets.filter((target) => target.kind === "asset").length}</strong><span>active asset targets</span><strong>{targets.filter((target) => target.kind === "part").length}</strong><span>part targets</span></div>

    <div className="dw-list">
      {filteredDocuments.map((document) => {
        const docTargetId = document.asset_id ?? document.part_id ?? "";
        const target = targetMap.get(docTargetId);
        const isImage = document.file_name ? /\.(png|jpe?g|webp|heic|gif)$/i.test(document.file_name) : false;
        return <article className="dw-card" key={document.id}>
          <div className="dw-icon">{isImage ? <Image size={22}/> : <FileText size={22}/>}</div>
          <div className="dw-main"><div className="dw-row"><span className="dw-type">{typeLabel(document.document_type)}</span>{document.revision && <span className="dw-rev">Rev {document.revision}</span>}</div><h2>{document.title}</h2><p>{target?.label ?? "Linked record unavailable"}</p><small>{document.file_name || document.external_url || "Document reference"} · {new Date(document.created_at).toLocaleString()}</small>{document.notes && <div className="dw-notes">{document.notes}</div>}</div>
          <div className="dw-actions"><button onClick={() => void viewDocument(document)}>{document.external_url ? <ExternalLink size={15}/> : <FileText size={15}/>} View</button><button onClick={() => void editMetadata(document)}><Pencil size={15}/> Edit</button>{DELETE_ROLES.has(role) && <button className="danger" onClick={() => void removeDocument(document)}><Trash2 size={15}/> Delete</button>}</div>
        </article>;
      })}
      {!filteredDocuments.length && <div className="dw-empty"><FilePlus2 size={32}/><h3>No documents here yet</h3><p>Upload a nameplate photo, manual, drawing, quote, repair report, or part datasheet and attach it to the exact plant record.</p></div>}
    </div>
  </div>;
}
